import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Pill } from "../../components/common/Primitives";
import api, { formatApiError, API_BASE } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Landmark, Download, ArrowLeft, RefreshCw, ChevronRight, MessageSquare, CheckCircle2, XCircle, Users, Phone, ClipboardList, FileText, StickyNote, GitBranch } from "lucide-react";
import { OwnerPicker, ClientPicker, CallLogPanel, PdFormPanel, LenderAppsPanel } from "../../components/debt/DebtCaseCrmPanels";
import { MandatePanel, BureauPanel, CamLitePanel } from "../../components/debt/DebtCaseAdvancedPanels";
import DebtCaseChatPanel from "../../components/debt/DebtCaseChatPanel";
import AdvanceGateModal, { isGated } from "../../components/debt/AdvanceGateModal";

const DROPPED = "Dropped";

export default function AdminDebtCases() {
  const { id } = useParams();
  return id ? <CaseDetail caseId={id} /> : <PipelineBoard />;
}

/* ------------------------------------------------------------------ */
/* Board view — column per pipeline stage                              */
/* ------------------------------------------------------------------ */
function PipelineBoard() {
  const [items, setItems] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cases, sum] = await Promise.all([
        api.get("/debt/cases", { params: { limit: 500 } }),
        api.get("/admin/debt/summary").catch(() => ({ data: null })),
      ]);
      setItems(cases.data?.items || []);
      setPipeline(cases.data?.pipeline || []);
      setCounts(cases.data?.stage_counts || {});
      setSummary(sum.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    (pipeline || []).forEach((s) => { g[s] = []; });
    g[DROPPED] = [];
    (items || []).forEach((c) => {
      const s = g[c.stage] ? c.stage : DROPPED;
      g[s].push(c);
    });
    return g;
  }, [items, pipeline]);

  // Advance-gate modal state — populated when a user clicks "Advance"
  // on a Kanban chip AND the target stage is in GATED_STAGES. Otherwise
  // we transition directly without a modal.
  const [gate, setGate] = useState({ open: false, caseId: null, caseUid: null, targetStage: null, submitting: false });

  const _doAdvance = useCallback(async (caseId, targetStage, reason) => {
    try {
      await api.post(`/debt/cases/${caseId}/stage`, { stage: targetStage, reason: reason || undefined });
      toast.success(`Advanced → ${targetStage}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  }, [load]);

  // Inline stage advance from a Kanban card. Gated stages open the modal
  // and wait for the mandatory note; ungated stages transition immediately.
  const advanceCase = useCallback(async (caseId, currentStage, caseUid) => {
    const idx = pipeline.indexOf(currentStage);
    if (idx < 0 || idx >= pipeline.length - 1) {
      toast.info("Already at the final stage");
      return;
    }
    const next = pipeline[idx + 1];
    if (isGated(next)) {
      setGate({ open: true, caseId, caseUid, targetStage: next, submitting: false });
      return;
    }
    try { await _doAdvance(caseId, next); } catch { /* toast surfaced */ }
  }, [pipeline, _doAdvance]);

  const submitGate = useCallback(async (note) => {
    setGate((g) => ({ ...g, submitting: true }));
    try {
      await _doAdvance(gate.caseId, gate.targetStage, note);
      setGate({ open: false, caseId: null, caseUid: null, targetStage: null, submitting: false });
    } catch {
      setGate((g) => ({ ...g, submitting: false }));
    }
  }, [gate, _doAdvance]);

  return (
    <DashboardShell title="Debt Cases" subtitle="VenturaZ Money · Pipeline">
      <Toaster richColors position="top-right" />
      <div className="space-y-6" data-testid="admin-debt-cases">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total cases" value={summary?.total_cases ?? "—"} />
          <Stat label="Total ask (₹)" value={summary ? fmtINR(summary.total_ask_inr) : "—"} />
          <Stat label="Disbursed (₹)" value={summary ? fmtINR(summary.disbursed_amount_inr) : "—"} tone="success" />
          <Stat label="Open lenders" value={
            <Link to="/admin/lenders" className="hover:underline inline-flex items-center gap-1">
              <Users size={14} /> Manage
            </Link>
          } />
        </div>

        <div className="flex items-center justify-between">
          <div className="text-xs text-ink-muted">Drag not enabled — open a case to move it through the pipeline.</div>
          <Button onClick={load} testId="debt-refresh"><RefreshCw size={12} className="inline mr-1" />Refresh</Button>
        </div>

        {loading ? (
          <div className="text-sm text-ink-muted italic">Loading pipeline…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
            {(pipeline || []).concat([DROPPED]).map((stage) => (
              <div key={stage} className="border border-navy/10 rounded bg-white/60 flex flex-col" data-testid={`debt-column-${stage}`}>
                <div className="px-3 py-2 border-b border-navy/[0.06] flex items-center justify-between">
                  <div className="text-[11px] font-heading uppercase tracking-widest text-ink">{stage}</div>
                  <span className="text-[10px] font-mono text-ink-muted">{(grouped[stage] || []).length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[80px] max-h-[520px] overflow-y-auto">
                  {(grouped[stage] || []).length === 0 ? (
                    <div className="text-[11px] italic text-ink-muted py-3 text-center">No cases</div>
                  ) : (
                    (grouped[stage] || []).map((c) => (
                      <div
                        key={c.case_id}
                        data-testid={`debt-card-${c.case_id}`}
                        className="block border border-navy/[0.08] hover:border-navy/25 rounded p-2 bg-white/70 transition-colors"
                      >
                        <Link to={`/admin/debt/${c.case_id}`} className="block" data-testid={`debt-card-link-${c.case_id}`}>
                          <div className="text-sm font-heading text-ink truncate">{c.company_name}</div>
                          <div className="text-[10px] font-mono text-ink-muted mt-0.5">
                            ₹{fmtINR(c.amount_inr)} · {c.purpose} · {c.tenure_months}m
                          </div>
                          <div className="text-[10px] font-mono text-ink-muted mt-0.5 truncate">
                            {c.contact_email}
                          </div>
                          {c.assigned_to_email && (
                            <div className="text-[10px] font-mono text-emerald-800 mt-0.5 truncate">Owner: {c.assigned_to_email}</div>
                          )}
                        </Link>
                        {/* Quick chips — same actions as the case-detail Quick
                            Actions bar, but without leaving the Kanban. */}
                        <div className="mt-2 pt-2 border-t border-navy/[0.06] flex items-center gap-1">
                          <Link
                            to={`/admin/debt/${c.case_id}#qa-call`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-navy/20 bg-white text-[10px] font-heading text-navy hover:bg-navy hover:text-cream transition-colors"
                            data-testid={`debt-card-log-call-${c.case_id}`}
                          >
                            <Phone size={10} /> Log Call
                          </Link>
                          {stage !== DROPPED && pipeline.indexOf(stage) < (pipeline.length - 1) && (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); advanceCase(c.case_id, c.stage, c.case_uid); }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gold text-navy text-[10px] font-heading font-semibold hover:bg-yellow-400"
                              data-testid={`debt-card-advance-${c.case_id}`}
                            >
                              <CheckCircle2 size={10} /> Advance
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <AdvanceGateModal
        open={gate.open}
        caseUid={gate.caseUid}
        targetStage={gate.targetStage}
        submitting={gate.submitting}
        onCancel={() => setGate({ open: false, caseId: null, caseUid: null, targetStage: null, submitting: false })}
        onSubmit={submitGate}
      />
    </DashboardShell>
  );
}

/* ------------------------------------------------------------------ */
/* Case detail — right-hand admin workspace                            */
/* ------------------------------------------------------------------ */
function CaseDetail({ caseId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lenders, setLenders] = useState([]);
  const [showLenderPicker, setShowLenderPicker] = useState(false);
  const [note, setNote] = useState("");
  const [noteKind, setNoteKind] = useState("note");
  const [transitioning, setTransitioning] = useState(false);
  // Case-detail advance-gate — mirror of the Kanban list flow so the
  // note capture UX is identical from either surface.
  const [gate, setGate] = useState({ open: false, targetStage: null, submitting: false });
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [dropReason, setDropReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/debt/cases/${caseId}`);
      setData(r.data);
      setEdit({
        amount_inr: r.data.case.amount_inr || 0,
        tenure_months: r.data.case.tenure_months || 0,
        expected_interest_rate_pa: r.data.case.expected_interest_rate_pa || "",
        notes: r.data.case.notes || "",
      });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  // Honour a `#qa-<panel>` hash from a Kanban chip click by scrolling
  // the target panel into view after the case-detail data mounts.
  useEffect(() => {
    if (loading || !data) return;
    const hash = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "");
    if (!hash?.startsWith("qa-")) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [loading, data]);

  const loadLenders = async () => {
    try {
      const r = await api.get("/admin/lenders", { params: { active_only: true } });
      setLenders(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const transition = async (stage, note) => {
    if (stage === DROPPED && !dropReason.trim()) {
      toast.error("Please add a reason before dropping this case."); return;
    }
    setTransitioning(true);
    try {
      const reason = stage === DROPPED ? dropReason.trim() : (note || null);
      await api.post(`/debt/cases/${caseId}/stage`, { stage, reason });
      toast.success(`Moved to ${stage}`);
      setDropReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
    finally { setTransitioning(false); }
  };

  // Guard for any stage transition initiated from the Advance CTA. Gated
  // stages open the modal; the modal's submit calls back into transition
  // with the captured note.
  const transitionMaybeGated = (stage) => {
    if (isGated(stage)) {
      setGate({ open: true, targetStage: stage, submitting: false });
      return;
    }
    transition(stage).catch(() => { /* toast surfaced */ });
  };

  const submitGate = async (note) => {
    setGate((g) => ({ ...g, submitting: true }));
    try {
      await transition(gate.targetStage, note);
      setGate({ open: false, targetStage: null, submitting: false });
    } catch {
      setGate((g) => ({ ...g, submitting: false }));
    }
  };

  const saveEdits = async () => {
    try {
      const body = {
        amount_inr: Number(edit.amount_inr) || null,
        tenure_months: Number(edit.tenure_months) || null,
        expected_interest_rate_pa: edit.expected_interest_rate_pa ? Number(edit.expected_interest_rate_pa) : null,
        notes: edit.notes || null,
      };
      Object.keys(body).forEach((k) => body[k] === null && delete body[k]);
      await api.patch(`/debt/cases/${caseId}`, body);
      toast.success("Case updated");
      setEditing(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const postNote = async () => {
    if (note.trim().length < 2) return;
    try {
      await api.post(`/debt/cases/${caseId}/notes`, { body: note.trim(), kind: noteKind });
      setNote("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleShortlist = async (lenderId) => {
    const current = new Set(data.case.shortlisted_lender_ids || []);
    if (current.has(lenderId)) current.delete(lenderId); else current.add(lenderId);
    try {
      await api.post(`/debt/cases/${caseId}/shortlist`, { lender_ids: Array.from(current) });
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading || !data) {
    return <DashboardShell title="Debt Case" subtitle="Loading…"><div className="text-sm text-ink-muted italic">Loading…</div></DashboardShell>;
  }
  const c = data.case;
  const idxCurrent = (data.pipeline || []).indexOf(c.stage);
  const nextStage = idxCurrent >= 0 && idxCurrent < (data.pipeline.length - 1) ? data.pipeline[idxCurrent + 1] : null;

  return (
    <DashboardShell title={c.company_name} subtitle={`Debt Case · ${c.case_id}`}>
      <Toaster richColors position="top-right" />
      <div className="space-y-4" data-testid={`admin-debt-case-${caseId}`}>
        <Link to="/admin/debt" className="text-xs text-navy hover:underline inline-flex items-center gap-1" data-testid="debt-back-link">
          <ArrowLeft size={12} /> Back to pipeline
        </Link>

        {/* Quick Actions — sticky bar that jumps the agent to the panel they
            need without scrolling. Sits below the app header (top-16) so it
            stays visible while the workspace scrolls under it. */}
        <div className="sticky top-16 z-30 -mx-4 md:-mx-6 lg:-mx-8 px-4 md:px-6 lg:px-8 py-2 bg-cream/95 backdrop-blur border-b border-navy/[0.08]" data-testid="debt-quick-actions">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mr-1">Quick actions</span>
            {[
              { id: "qa-call",      label: "Log Call",       icon: Phone,         tid: "qa-log-call" },
              { id: "qa-pd",        label: "Log PD",         icon: ClipboardList, tid: "qa-log-pd" },
              { id: "qa-documents", label: "Request Docs",   icon: FileText,      tid: "qa-request-docs" },
              { id: "qa-notes",     label: "Add Note",       icon: StickyNote,    tid: "qa-add-note" },
              { id: "qa-stage",     label: "Change Stage",   icon: GitBranch,     tid: "qa-change-stage" },
            ].map(({ id, label, icon: Icon, tid }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  const el = document.getElementById(id);
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                    // Focus the first form control inside so the agent can start typing.
                    const focusable = el.querySelector('input, textarea, select, button');
                    if (focusable && focusable.tagName !== 'BUTTON') {
                      setTimeout(() => focusable.focus?.(), 400);
                    }
                  }
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-navy/20 bg-white text-[11px] font-heading text-navy hover:bg-navy hover:text-cream transition-colors"
                data-testid={tid}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
            {nextStage && (
              <button
                type="button"
                onClick={() => transitionMaybeGated(nextStage)}
                disabled={transitioning}
                className="ml-auto inline-flex items-center gap-1 px-3 py-1 rounded-md bg-gold text-navy text-[11px] font-heading font-semibold hover:bg-yellow-400 disabled:opacity-50"
                data-testid="qa-advance-stage"
              >
                <CheckCircle2 size={12} /> Advance → {nextStage}
              </button>
            )}
          </div>
        </div>

        {/* Header bar */}
        <div className="border border-navy/10 rounded bg-white/60 p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Pill tone="gold">{c.stage}</Pill>
                {c.status && <Pill tone="muted">{c.status}</Pill>}
                {c.case_uid && <span className="text-[10px] font-mono text-navy bg-navy/[0.05] px-2 py-0.5 rounded">{c.case_uid}</span>}
                {c.client_id && (
                  <Link to={`/admin/debt/clients/${c.client_id}`} className="text-[10px] font-mono text-emerald-800 bg-emerald-500/10 px-2 py-0.5 rounded hover:underline" data-testid="debt-case-client-link">
                    Client 360 →
                  </Link>
                )}
                <span className="text-[10px] font-mono text-ink-muted">Created {formatDateTime(c.created_at)} by {c.created_by_email}</span>
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <Info label="Amount" value={`₹${fmtINR(c.amount_inr)}`} />
                <Info label="Tenure" value={`${c.tenure_months} months`} />
                <Info label="Purpose" value={c.purpose} />
                <Info label="Security" value={c.security_type} />
                <Info label="Industry" value={c.industry || "—"} />
                <Info label="HQ state" value={c.hq_state || "—"} />
                <Info label="Revenue" value={c.annual_revenue_inr ? `₹${fmtINR(c.annual_revenue_inr)}` : "—"} />
                <Info label="Existing debt" value={c.existing_debt_inr ? `₹${fmtINR(c.existing_debt_inr)}` : "—"} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              {nextStage && (
                <Button variant="gold" onClick={() => transitionMaybeGated(nextStage)} disabled={transitioning} testId="debt-advance-btn">
                  Advance → {nextStage}
                </Button>
              )}
              <button onClick={() => setEditing((v) => !v)} className="text-[11px] px-2 py-1 border border-navy/25 rounded font-heading hover:bg-navy/5" data-testid="debt-edit-toggle">
                {editing ? "Cancel edit" : "Edit fields"}
              </button>
            </div>
          </div>
          {editing && (
            <div className="mt-4 pt-4 border-t border-navy/[0.06] grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="debt-edit-form">
              <div className="md:col-span-2">
                <OwnerPicker
                  caseId={caseId}
                  currentUserId={c.assigned_to_user_id}
                  currentEmail={c.assigned_to_email}
                  onChange={load}
                />
              </div>
              <div className="md:col-span-2">
                <ClientPicker
                  caseId={caseId}
                  currentClientId={c.client_id}
                  caseUid={c.case_uid}
                  onChange={load}
                />
              </div>
              <Field label="Amount INR" type="number" value={edit.amount_inr} onChange={(v) => setEdit((e) => ({ ...e, amount_inr: v }))} testId="debt-edit-amount" />
              <Field label="Tenure months" type="number" value={edit.tenure_months} onChange={(v) => setEdit((e) => ({ ...e, tenure_months: v }))} testId="debt-edit-tenure" />
              <Field label="Expected rate %" type="number" value={edit.expected_interest_rate_pa} onChange={(v) => setEdit((e) => ({ ...e, expected_interest_rate_pa: v }))} testId="debt-edit-rate" />
              <div className="md:col-span-2">
                <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Notes</label>
                <textarea rows={2} value={edit.notes} onChange={(e) => setEdit((v) => ({ ...v, notes: e.target.value }))}
                  className="w-full mt-0.5 px-3 py-2 border border-navy/15 rounded text-sm" data-testid="debt-edit-notes" />
              </div>
              <div className="md:col-span-2">
                <Button variant="gold" onClick={saveEdits} testId="debt-edit-save">Save changes</Button>
              </div>
            </div>
          )}
        </div>

        {/* Pipeline chips */}
        <div id="qa-stage" className="border border-navy/10 rounded bg-white/60 p-4 scroll-mt-24">
          <div className="overline mb-2">Pipeline</div>
          <div className="flex flex-wrap items-center gap-1">
            {(data.pipeline || []).map((s, i) => {
              const passed = i <= idxCurrent;
              const active = i === idxCurrent;
              return (
                <React.Fragment key={s}>
                  <button
                    onClick={() => transitionMaybeGated(s)}
                    disabled={transitioning}
                    data-testid={`debt-stage-${s.replace(/\s/g, "_")}`}
                    className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded ${
                      active
                        ? "bg-gold/25 text-[#8A6600] border border-gold/50"
                        : passed
                          ? "bg-emerald-500/10 text-emerald-800 hover:bg-emerald-500/20"
                          : "bg-navy/5 text-ink-muted hover:bg-navy/10"
                    }`}
                  >
                    {s}
                  </button>
                  {i < data.pipeline.length - 1 && <ChevronRight size={10} className="text-ink-muted" />}
                </React.Fragment>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-navy/[0.06] flex items-center gap-2 flex-wrap">
            <input
              type="text"
              placeholder="Reason (required to drop)"
              value={dropReason}
              onChange={(e) => setDropReason(e.target.value)}
              className="flex-1 min-w-[200px] px-2 py-1.5 border border-navy/15 rounded text-xs"
              data-testid="debt-drop-reason"
            />
            <button
              onClick={() => transition(DROPPED)}
              disabled={transitioning || !dropReason.trim()}
              className="text-[11px] font-heading text-rose-700 hover:bg-rose-500/10 rounded px-2 py-1 border border-rose-500/40 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="debt-drop-btn"
            >
              <XCircle size={11} /> Drop case
            </button>
          </div>
        </div>

        {/* Lenders shortlist */}
        <div className="border border-navy/10 rounded bg-white/60 p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="overline">Shortlisted lenders</div>
              <div className="text-[10px] font-mono text-ink-muted">Curate which lenders you&apos;ll pitch this case to.</div>
            </div>
            <button
              onClick={() => { setShowLenderPicker((v) => !v); if (lenders.length === 0) loadLenders(); }}
              className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30"
              data-testid="debt-lender-picker-toggle"
            >
              {showLenderPicker ? "Close picker" : "Pick lenders"}
            </button>
          </div>
          {(data.shortlisted_lenders || []).length === 0 ? (
            <div className="text-xs italic text-ink-muted">No lenders shortlisted yet.</div>
          ) : (
            <div className="space-y-1">
              {data.shortlisted_lenders.map((l) => (
                <div key={l.lender_id} className="flex items-center justify-between text-xs border border-navy/[0.06] rounded px-2 py-1" data-testid={`debt-shortlisted-${l.lender_id}`}>
                  <div>
                    <div className="font-heading text-ink">{l.name}</div>
                    <div className="text-[10px] font-mono text-ink-muted">{l.lender_type} · {l.contact_email || "—"}</div>
                  </div>
                  <button onClick={() => toggleShortlist(l.lender_id)} className="text-rose-700 hover:underline text-[10px]" data-testid={`debt-shortlist-remove-${l.lender_id}`}>Remove</button>
                </div>
              ))}
            </div>
          )}
          {showLenderPicker && (
            <div className="mt-3 pt-3 border-t border-navy/[0.06] max-h-[300px] overflow-y-auto" data-testid="debt-lender-picker">
              {lenders.length === 0 ? (
                <div className="text-xs italic text-ink-muted">
                  No active lenders. <Link to="/admin/lenders" className="text-navy underline">Add one</Link>.
                </div>
              ) : (
                <div className="space-y-1">
                  {lenders.map((l) => {
                    const on = (data.case.shortlisted_lender_ids || []).includes(l.lender_id);
                    return (
                      <label key={l.lender_id} className="flex items-center gap-2 text-xs border border-navy/[0.06] rounded px-2 py-1 hover:bg-navy/[0.02] cursor-pointer" data-testid={`debt-lender-row-${l.lender_id}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleShortlist(l.lender_id)} />
                        <div className="flex-1 min-w-0">
                          <div className="font-heading text-ink truncate">{l.name}</div>
                          <div className="text-[10px] font-mono text-ink-muted truncate">
                            {l.lender_type} · ₹{fmtINR(l.min_ticket_inr)}–{fmtINR(l.max_ticket_inr)} · {(l.geographies || []).join(", ") || "any"}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Documents */}
        <div id="qa-documents" className="border border-navy/10 rounded bg-white/60 p-4 scroll-mt-24">
          <div className="overline mb-2">Documents</div>
          {(data.documents || []).length === 0 ? (
            <div className="text-xs italic text-ink-muted">No documents uploaded yet.</div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {data.documents.map((d) => (
                <div key={d.doc_id} className="py-2 flex items-center justify-between" data-testid={`debt-admin-doc-${d.doc_id}`}>
                  <div>
                    <div className="text-sm font-heading text-ink">{d.label || d.filename}</div>
                    <div className="text-[10px] font-mono text-ink-muted">{d.doc_type} · {formatDateTime(d.uploaded_at)} · {d.uploaded_by_email}</div>
                  </div>
                  <a href={`${API_BASE}/debt/cases/${caseId}/documents/${d.doc_id}`} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-navy hover:underline inline-flex items-center gap-1" data-testid={`debt-admin-doc-dl-${d.doc_id}`}>
                    <Download size={12} /> Download
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CRM panels — call log · PD form · lender applications.
            Each wrapped in a targetable anchor for the Quick Actions bar. */}
        <div id="qa-call" className="scroll-mt-24"><CallLogPanel caseId={caseId} onChanged={load} /></div>
        <div id="qa-pd" className="scroll-mt-24"><PdFormPanel caseId={caseId} /></div>
        <LenderAppsPanel caseId={caseId} onChanged={load} />

        {/* Advanced — mandate e-sign · bureau · CAM-Lite */}
        <MandatePanel caseId={caseId} borrowerEmail={c.contact_email} borrowerName={c.contact_name} />
        <BureauPanel caseId={caseId} borrowerName={c.contact_name} borrowerPan={c.pan} />
        <CamLitePanel caseId={caseId} />

        {/* Internal debt-team back-channel — never visible to borrower */}
        <DebtCaseChatPanel caseId={caseId} caseUid={c.case_uid} />

        {/* Notes / timeline */}
        <div id="qa-notes" className="border border-navy/10 rounded bg-white/60 p-4 scroll-mt-24">
          <div className="overline mb-2">Notes & timeline</div>
          <div className="space-y-2 mb-3 max-h-[380px] overflow-y-auto">
            {(data.notes || []).map((n) => (
              <div key={n.note_id} className="border border-navy/[0.06] rounded p-2 text-xs" data-testid={`debt-admin-note-${n.note_id}`}>
                <div className="text-[10px] font-mono text-ink-muted mb-1 flex items-center gap-2">
                  <span>{n.author_email}</span>
                  <span className="text-ink-muted/60">·</span>
                  <span>{n.kind}</span>
                  <span className="text-ink-muted/60">·</span>
                  <span>{formatDateTime(n.at)}</span>
                </div>
                <div className="text-ink whitespace-pre-wrap">{n.body}</div>
              </div>
            ))}
            {(data.notes || []).length === 0 && <div className="text-xs italic text-ink-muted">No notes yet.</div>}
          </div>
          <div className="flex gap-2 items-center">
            <select value={noteKind} onChange={(e) => setNoteKind(e.target.value)} className="text-xs px-2 py-2 border border-navy/15 rounded bg-white" data-testid="debt-note-kind">
              {["note","call","email","meeting","lender_response"].map((k) => <option key={k}>{k}</option>)}
            </select>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note / log activity…"
              className="flex-1 px-3 py-2 border border-navy/15 rounded text-sm" data-testid="debt-admin-note-input" />
            <Button onClick={postNote} testId="debt-admin-note-submit"><MessageSquare size={12} className="inline mr-1" />Post</Button>
          </div>
        </div>

        {/* Events */}
        <div className="border border-navy/10 rounded bg-white/60 p-4">
          <div className="overline mb-2">Activity log</div>
          <div className="max-h-[300px] overflow-y-auto space-y-1 text-[11px] font-mono">
            {(data.events || []).map((e) => (
              <div key={e.event_id} className="flex items-start gap-2 text-ink-muted border-b border-navy/[0.05] py-1" data-testid={`debt-event-${e.event_id}`}>
                <span className="text-[10px] shrink-0">{formatDateTime(e.at)}</span>
                <span className="text-navy">{e.kind}</span>
                <span className="text-ink-muted/70 truncate">{e.actor_email}</span>
              </div>
            ))}
            {(data.events || []).length === 0 && <div className="italic">No events yet.</div>}
          </div>
        </div>
      </div>
      <AdvanceGateModal
        open={gate.open}
        caseUid={c?.case_uid}
        targetStage={gate.targetStage}
        submitting={gate.submitting}
        onCancel={() => setGate({ open: false, targetStage: null, submitting: false })}
        onSubmit={submitGate}
      />
    </DashboardShell>
  );
}

/* ------------------------------------------------------------------ */
/* Small primitives                                                    */
/* ------------------------------------------------------------------ */
function Stat({ label, value, tone }) {
  return (
    <div className={`border border-navy/10 rounded p-3 bg-white/60`} data-testid={`debt-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
      <div className={`font-display text-xl mt-1 ${tone === "success" ? "text-emerald-800" : "text-ink"}`}>{value}</div>
    </div>
  );
}
function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="font-heading text-sm text-ink mt-0.5 break-words">{value}</div>
    </div>
  );
}
function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} type={type} data-testid={testId}
        className="w-full mt-0.5 px-3 py-2 border border-navy/15 rounded text-sm" />
    </div>
  );
}

function fmtINR(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num === 0) return "0";
  return num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
